<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal" role="dialog" aria-modal="true" aria-label="Уведомления">
      <div class="modal-header">
        <h3>Уведомления</h3>
        <button type="button" class="btn-close" aria-label="Закрыть" @click="$emit('close')">&times;</button>
      </div>
      <div class="modal-body">
        <p v-if="loading" class="muted">Загрузка...</p>

        <template v-else>
          <p v-if="!serverOn" class="note">Уведомления пока не настроены на сервере. Обратитесь в поддержку.</p>
          <p v-else-if="blocker" class="note">{{ BLOCKER_TEXT[blocker] }}</p>

          <div v-else class="row">
            <div>
              <div class="row-title">Уведомления на этом устройстве</div>
              <div class="muted">Новые записи, отмены и переносы от клиентов</div>
            </div>
            <SettingsSwitch :model-value="thisOn" :disabled="busy" label="Уведомления на этом устройстве" @update:model-value="toggle" />
          </div>

          <div v-if="canInstall" class="row">
            <div>
              <div class="row-title">Установить приложение</div>
              <div class="muted">Откроется с экрана без адресной строки</div>
            </div>
            <button type="button" class="btn-sec" @click="install">Установить</button>
          </div>

          <div v-if="devices.length" class="devices">
            <div class="row-title">Устройства с уведомлениями</div>
            <div v-for="d in devices" :key="d.id" class="device">
              <div>
                <div>{{ d.device_label || 'Устройство' }}<span v-if="d.endpoint_hash === myHash" class="me"> это устройство</span></div>
                <div class="muted">
                  Подключено {{ fmt(d.created_at) }}<template v-if="d.last_used_at">, последнее уведомление {{ fmt(d.last_used_at) }}</template>
                </div>
                <div v-if="d.last_error" class="err">Последняя отправка не удалась</div>
              </div>
              <button type="button" class="btn-link" @click="remove(d)">Отключить</button>
            </div>
          </div>
        </template>
      </div>
      <div class="modal-footer">
        <button v-if="thisOn" type="button" class="btn-sec" :disabled="busy" @click="test">Отправить проверочное</button>
        <button type="button" class="btn-sec" @click="$emit('close')">Закрыть</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { api } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import SettingsSwitch from './settings/SettingsSwitch.vue'
import {
  pushBlocker, BLOCKER_TEXT, getPushConfig, currentSubscription, endpointHash, enablePush, disablePush,
  canPromptInstall, promptInstall, onPwaChange
} from '../utils/pwa'

defineEmits(['close'])
const toast = useToastStore()
const loading = ref(true)
const busy = ref(false)
const serverOn = ref(false)
const thisOn = ref(false)
const myHash = ref(null)
const devices = ref([])
const tick = ref(0)
const blocker = computed(() => (tick.value, pushBlocker()))
const canInstall = computed(() => (tick.value, canPromptInstall()))
onPwaChange(() => { tick.value++ })

const fmt = (iso) => new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

async function load() {
  try {
    const [cfg, sub, list] = await Promise.all([
      getPushConfig(), pushBlocker() ? null : currentSubscription(), api.get('/push/devices')
    ])
    serverOn.value = !!cfg.enabled
    devices.value = list.data
    myHash.value = await endpointHash(sub)
    thisOn.value = !!sub && devices.value.some(d => d.endpoint_hash === myHash.value)
  } catch { toast.error('Не удалось загрузить список устройств') } finally { loading.value = false }
}

async function toggle(on) {
  busy.value = true
  try {
    if (on) { await enablePush(); toast.success('Уведомления включены') }
    else { await disablePush(); toast.success('Уведомления на этом устройстве выключены') }
    await load()
  } catch (e) { toast.error(e.message || 'Не получилось изменить настройку'); tick.value++ } finally { busy.value = false }
}

async function remove(d) {
  if (d.endpoint_hash === myHash.value) return toggle(false)
  try { await api.delete(`/push/devices/${d.id}`); await load() } catch { toast.error('Не удалось отключить устройство') }
}

async function test() {
  busy.value = true
  try {
    const { data } = await api.post('/push/test')
    data.sent ? toast.success('Проверочное уведомление отправлено') : toast.warning('Уведомление не доставлено, попробуйте включить заново')
  } catch (e) { toast.error(e.response?.data?.error || 'Не удалось отправить') } finally { busy.value = false }
}

async function install() { await promptInstall(); tick.value++ }
onMounted(load)
</script>

<style scoped>
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; padding: 1rem; z-index: 1000; overflow-y: auto; }
.modal { background: #fff; border-radius: 16px; width: 100%; max-width: 480px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,.2); }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 1.25rem; border-bottom: 1px solid #f3f4f6; }
.modal-header h3 { font-size: 1.1rem; font-weight: 600; color: #1f2937; margin: 0; }
.btn-close { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; background: #f3f4f6; border: none; border-radius: 10px; cursor: pointer; font-size: 1.1rem; color: #6b7280; }
.btn-close:hover { background: #e5e7eb; color: #374151; }
.modal-body { padding: 1.25rem; display: flex; flex-direction: column; gap: 1rem; }
.modal-footer { display: flex; justify-content: flex-end; gap: .75rem; padding: 1rem 1.25rem; border-top: 1px solid #f3f4f6; background: #f9fafb; flex-wrap: wrap; }
.row { display: flex; justify-content: space-between; align-items: center; gap: 1rem; }
.row-title { font-size: .95rem; font-weight: 500; color: #1f2937; }
.muted { font-size: .8rem; color: #6b7280; }
.note { font-size: .88rem; color: #475569; background: var(--brand-primary-soft); border-radius: 10px; padding: .75rem 1rem; line-height: 1.45; }
.devices { display: flex; flex-direction: column; gap: .5rem; border-top: 1px solid #f3f4f6; padding-top: 1rem; }
.device { display: flex; justify-content: space-between; align-items: center; gap: 1rem; padding: .5rem 0; font-size: .9rem; color: #1f2937; }
.me { color: var(--brand-primary); font-size: .8rem; }
.err { font-size: .78rem; color: #b45309; }
.btn-sec { padding: .55rem 1rem; border: 1px solid #cbd5e1; background: #fff; color: #475569; border-radius: 10px; cursor: pointer; font-size: .85rem; }
.btn-sec:hover:not(:disabled) { background: #f1f5f9; }
.btn-sec:disabled { opacity: .6; cursor: default; }
.btn-link { background: none; border: none; color: #64748b; cursor: pointer; font-size: .85rem; text-decoration: underline; flex-shrink: 0; }
</style>
